import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { instance, pathname } from "./ActionCreators";
import axios from "axios";
import { RootState } from "../store";
import notifySuccess from "utils/Notifications/Success";
import notifyError from "utils/Notifications/Error";
import Error from "../../utils/Notifications/Error";
import {
    getTickerSettings,
    resetGroup,
    resetTicker,
    resetTickerSettings,
    setSelectedGroup,
    setSelectedTicker,
} from "./SettingsSlice";
import { setPendingCommand } from "./LogsSlice";

type Group = { name: string; id: number };
type Tickers = {
    name: {
        index: number;
        schedule_id: string;
        ticker_name: string;
        hedgeinst_id: string;
        comment: string | null;
    };
    isNew: boolean;
}[];

type IState = {
    groupsLoading: boolean;
    tickersLoading: boolean;
    error: null | string;
    groups: Group[];
    activeGroupTickers: Tickers;
    defaultTickers: string[];
    currentGroupName: string;
};

const initialState: IState = {
    groupsLoading: false,
    tickersLoading: false,
    error: null,
    groups: [],
    activeGroupTickers: [],
    defaultTickers: [],
    currentGroupName: "",
};

export const getGroups = createAsyncThunk(
    "get/groups",
    async (_, { rejectWithValue }) => {
        try {
            const response = await instance.get(pathname + "/api/groups");
            return response.data.map((name: string, index: number) => {
                return {
                    name,
                    id: index,
                };
            });
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    },
);

export const getTickersByGroup = createAsyncThunk(
    "get/tickersByGroup",
    async (groupName: string, { rejectWithValue }) => {
        try {
            const normalizedGroupName =
                typeof groupName === "string" ? groupName : groupName[0];
            const response = await instance.get(
                `${pathname}/api/ticker/${encodeURIComponent(
                    normalizedGroupName,
                )}`,
            );
            return response.data.map((item: any) => ({
                name: {
                    index: item.index,
                    schedule_id: item.schedule_id,
                    ticker_name: item.ticker_name,
                    hedgeinst_id: item.hedgeinst_id,
                    comment: item.comment,
                },
                isNew: false,
            }));
        } catch (e) {
            // @ts-ignore
            if (e.response.status === 409) {
                notifyError("Тикер редактируется другим пользователем");
            }
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    },
);

export const getDefaultTickers = createAsyncThunk(
    "get/defaultTickers",
    async (_, { rejectWithValue }) => {
        try {
            const response = await instance.get(
                pathname + `/api/ticker/default`,
            );
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    },
);

function rtrim(str) {
    if (!str) return str;
    return str.replace(/\s+$/g, "");
}

export const createGroup = createAsyncThunk(
    "create/group",
    async (
        { name, type }: { name: string; type: string | undefined },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const { groups } = getState() as RootState;

            if (
                groups.groups.filter((it: { name: string }) => it.name === name)
                    .length === 0
            ) {
                let extraSpaceName = rtrim(name);
                const groupName =
                    type === "copy"
                        ? `${extraSpaceName} - копия 1`
                        : extraSpaceName;

                const body = {
                    name: groupName,
                };

                const response = await instance.post(
                    pathname + "/api/groups",
                    body,
                );
                notifySuccess("Группа успешно создана");
                dispatch(getGroups());
                return response.data;
            } else {
                dispatch(getGroups());
                Error("Группа с таким названием уже существует");
            }
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    },
);

export const copyGroup = createAsyncThunk(
    "copy/group",
    async (
        {
            groupCopyName,
            groupName,
        }: { groupCopyName: string; groupName: string },
        { dispatch },
    ) => {
        try {
            const body = {
                group_name: groupName,
                target_group_name: groupCopyName,
            };
            const response = await instance.post(
                pathname + "/api/copy_group",
                body,
            );
            notifySuccess(
                `Создана копия группы ${groupName} с именем ${groupCopyName}`,
            );

            dispatch(getGroups());
            return response;
        } catch (e) {
            notifyError(
                Object.values(e.response?.data || {})
                    .flat()
                    .join(" ") ||
                    e.message ||
                    "Произошла ошибка",
            );
        }
    },
);

export const updateTickerComment = createAsyncThunk(
    "groups/updateTickerComment",
    async (
        {
            group_name,
            ticker_name,
            comment,
        }: {
            group_name: string;
            ticker_name: string;
            comment: string;
        },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const body = {
                ticker_name,
                comment: comment.trim(),
            };

            const response = await instance.patch(
                `${pathname}/api/groups/${group_name}`,
                body,
            );

            notifySuccess("Комментарий успешно обновлён");

            dispatch(getTickersByGroup(group_name));

            return response.data;
        } catch (e: any) {
            if (axios.isAxiosError(e)) {
                const message = e.response?.data?.detail || e.message;
                notifyError(message);
                return rejectWithValue(message);
            }
            notifyError("Ошибка при обновлении комментария");
            return rejectWithValue(e);
        }
    },
);

export const renameGroup = createAsyncThunk(
    "rename/group",
    async (_name: string, { rejectWithValue, dispatch, getState }) => {
        const name = _name.slice().trim();
        const { settings, groups } = getState() as RootState;
        const { activeGroup } = settings;
        try {
            if (
                groups.groups.filter((it: { name: string }) => it.name === name)
                    .length === 0
            ) {
                const body = {
                    group_name: name,
                };
                const response = await instance.patch(
                    pathname + `/api/groups/${activeGroup.name}`,
                    body,
                );
                notifySuccess("Группа успешно переименована");
                dispatch(getGroups());
                dispatch(setSelectedGroup({ name, id: activeGroup?.id }));
                dispatch(getTickersByGroup(name));
                return response.data;
            } else {
                dispatch(getGroups());
                dispatch(setSelectedGroup(activeGroup));
                Error("Группа с таким названием уже существует");
            }
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    },
);

export const deleteGroup = createAsyncThunk(
    "delete/tabs",
    async (name: string, { rejectWithValue, dispatch }) => {
        try {
            let apiString = `/api/groups/${name} `;
            // let extraSpaceName = name[name.length - 1] === " " ? name.slice(0, name.length - 1) : name
            const response = await instance.delete(pathname + apiString);
            notifySuccess("Группа удалена");
            dispatch(resetGroup());
            dispatch(resetGroupTickers());
            dispatch(getGroups());
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                if (e.response.data.name) {
                    Error(e.response.data.name);
                }
                return rejectWithValue(e.message);
            }
        }
    },
);

export const applyGroup = createAsyncThunk(
    "apply/group",
    async (
        {
            groupName,
            config_ids,
        }: { groupName: string; config_ids?: (number | string)[] },
        { rejectWithValue, dispatch, getState },
    ) => {
        try {
            const body = {
                group_name: groupName,
                config_ids,
            };
            const response = await instance.post(
                pathname + "/api/apply_group",
                body,
            );
            if (config_ids?.length) {
                const { settings } = getState() as RootState;
                const fileNames = (settings.applyFiles || [])
                    .filter((file: { id: number | string }) =>
                        config_ids.includes(file.id),
                    )
                    .map((file: { name: string }) => file.name)
                    .join(", ");

                if (fileNames) {
                    notifySuccess(
                        `строки из ${fileNames} отправлены в текущую группу`,
                    );
                }
            }
            const items = Array.isArray(response.data?.tcp_queues)
                ? response.data.tcp_queues
                : Array.isArray(response.data)
                  ? response.data
                  : [];

            items.forEach(
                (item: { tcp_queue_id: number; ticker_name: string }) => {
                    if (Number.isFinite(item.tcp_queue_id)) {
                        dispatch(
                            setPendingCommand({
                                tcp_queue_id: item.tcp_queue_id,
                                ticker_name: item.ticker_name,
                                command_type: "apply_group",
                            }),
                        );
                    }
                },
            );
            dispatch(getGroups());
            notifySuccess("Группа успешно применена");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );
                return rejectWithValue(e.message);
            }
        }
    },
);

export const applyTicker = createAsyncThunk(
    "apply/ticker",
    async (
        {
            tickerName,
            config_ids,
            schedule_id,
            index,
            bodyObject = { for_all: false },
            skipSocket = false,
        }: {
            tickerName: string | null;
            bodyObject?: {
                for_all: boolean;
                cp_columns?: string[];
                mdp_columns?: string[];
            };
            config_ids?: (number | string)[];
            schedule_id?: string;
            index?: number;
            skipSocket?: boolean;
        },
        { rejectWithValue, getState, dispatch },
    ) => {
        const { settings, groups } = getState() as RootState;
        // const {name: groupName} = settings.activeGroup
        const { activeTicker: ticker, activeGroup: group } = settings;
        const { defaultTickers } = groups;
        const actualTickerName = tickerName ?? ticker.name;
        const isTickerNew = !defaultTickers.includes(actualTickerName);
        const actualScheduleId =
            schedule_id !== undefined ? schedule_id : ticker.schedule_id;
        const actualIndex = index !== undefined ? index : ticker.index;
        try {
            const body = {
                group_name: group.name,
                ticker_name: actualTickerName,
                ...(config_ids?.length ? { config_ids } : {}),
                is_new: isTickerNew,
                ...(actualScheduleId ? { schedule_id: actualScheduleId } : {}),
                ...(actualIndex !== undefined && actualIndex !== -1
                    ? { index: actualIndex }
                    : {}),
                ...bodyObject,
            };
            const response = await instance.post(
                pathname + "/api/apply/ticker",
                body,
            );
            if (!skipSocket) {
                if (config_ids?.length) {
                    const { settings } = getState() as RootState;
                    const fileNames = (settings.applyFiles || [])
                        .filter((file: { id: number | string }) =>
                            config_ids.includes(file.id),
                        )
                        .map((file: { name: string }) => file.name)
                        .join(", ");

                    if (fileNames) {
                        notifySuccess(
                            `строки из ${fileNames} отправлены в тикер ${actualTickerName}`,
                        );
                    }
                }
                const items = Array.isArray(response.data?.tcp_queues)
                    ? response.data.tcp_queues
                    : Array.isArray(response.data)
                      ? response.data
                      : [];

                items.forEach(
                    (item: { tcp_queue_id: number; ticker_name: string }) => {
                        if (Number.isFinite(item.tcp_queue_id)) {
                            dispatch(
                                setPendingCommand({
                                    tcp_queue_id: item.tcp_queue_id,
                                    ticker_name: item.ticker_name,
                                    command_type: "apply_ticker",
                                    is_mass: bodyObject.for_all ?? false,
                                }),
                            );
                        }
                    },
                );
            }
            dispatch(getDefaultTickers());

            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );
                return rejectWithValue(e.message);
            }
        }
    },
);

export const copyTicker = createAsyncThunk(
    "copy/ticker",
    async (
        tickerData: { name: string; schedule_id: string; index: number },
        { rejectWithValue, getState },
    ) => {
        const { settings } = getState() as RootState;
        const { name: groupName } = settings.activeGroup;
        try {
            const body = {
                group_name: groupName,
                ticker_name: tickerData.name,
                schedule_id: tickerData.schedule_id,
                index: tickerData.index,
            };
            const response = await instance.post(
                `${pathname}/api/duplicate_ticker`,
                body,
            );

            notifySuccess("Тикер успешно скопирован");

            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );
                return rejectWithValue(e.message);
            }
        }
    },
);

export const renameTicker = createAsyncThunk(
    "rename/ticker",
    async (_name: string, { rejectWithValue, getState, dispatch }) => {
        const name = _name.slice().trim();
        const { settings, groups } = getState() as RootState;
        const { activeTicker, activeGroup } = settings;
        try {
            if (
                groups.activeGroupTickers.filter(
                    (it) => it.name.ticker_name === name,
                ).length === 0
            ) {
                const body = {
                    ticker_name: name,
                };
                const response = await instance.patch(
                    pathname +
                        `/api/ticker/${activeTicker.name}?group_name=${activeGroup.name}`,
                    body,
                );
                notifySuccess("Тикер успешно переименован");
                dispatch(getGroups());
                dispatch(
                    setSelectedGroup({
                        name: activeGroup?.name,
                        id: activeGroup?.id,
                    }),
                );
                dispatch(setSelectedTicker({ name }));
                dispatch(getTickersByGroup(activeGroup?.name));

                const timeout = setTimeout(() => {
                    dispatch(getTickerSettings());
                    return clearTimeout(timeout);
                }, 200);
                return response.data;
            } else {
                Error("Тикер с таким названием уже существует");
            }
        } catch (e) {
            if (axios.isAxiosError(e)) {
                const msg =
                    typeof e.response?.data === "object"
                        ? JSON.stringify(e.response.data)
                        : e.response?.data || e.message;

                Error(msg);
                return rejectWithValue(e.message);
            }
        }
    },
);

export const createTicker = createAsyncThunk(
    "create/ticker",
    async (tickerName: string, { rejectWithValue, getState }) => {
        const { settings } = getState() as RootState;
        const { name: groupName } = settings.activeGroup;
        try {
            const body = {
                group_name: groupName,
                ticker_name: tickerName,
                CP: [],
                MDP: [],
                SP: [],
            };
            const response = await instance.post(
                pathname + "/api/ticker",
                body,
            );
            notifySuccess("Тикер успешно создан");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                notifyError(
                    Object.values(e.response?.data || {})
                        .flat()
                        .join(" ") ||
                        e.message ||
                        "Произошла ошибка",
                );
                return rejectWithValue(e.message);
            }
        }
    },
);

export const addTicker = createAsyncThunk(
    "add/ticker",
    async (tickerName: string, { rejectWithValue, getState }) => {
        const { settings } = getState() as RootState;
        const { name: groupName } = settings.activeGroup;
        try {
            const body = {
                group_name: groupName,
                ticker_name: tickerName,
                CP: [
                    [
                        "",
                        "ID.depend",
                        "Cotir.def.param.group.ID",
                        "BidUncrossOffset",
                        "AskUncrossOffset",
                        "VolumeWeight",
                        "NoVolumePenalty",
                        "D.Spread",
                        "NormalSpread",
                        "QuoteVolume",
                        "D_oc",
                        "GO.HardOpen",
                        "moveVol",
                        "movePrice",
                        "eLimitedMargin",
                        "BUY",
                        "SprOpen",
                        "SprClose",
                        "StepPos",
                        "StepSpread",
                        "NoShiftLimit",
                        "LimitBuy",
                        "SELL",
                        "SprOpen",
                        "SprClose",
                        "StepPos",
                        "StepSpread",
                        "NoShiftLimit",
                        "LimitSell",
                        "HEDGING",
                        "IsHedgeble",
                        "Priority",
                        "MinPos",
                        "OrderVol",
                        "PriceDistance",
                        "SpreadCoefDistance",
                        "UseTeorPrices",
                        "HedgeInstId",
                    ],
                    [
                        "1",
                        `${tickerName}~DEP~SPB`,
                        "TEST",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "AAL~HEDGE~BIN",
                    ],
                ],
                MDP: [
                    [
                        "SheduleID",
                        "ID.depend",
                        "ID.source",
                        "Source.Type",
                        "Source.type.shedule.ID",
                    ],
                ],
                SP: [
                    [
                        "ID.source",
                        "Source.def.par.group.ID",
                        "DealsWindow..sec.",
                        "DealsVolRecalcShare",
                        "Currency",
                        "ShortBonus",
                        "NotTradable",
                        "Factor",
                        "Beta",
                        "BaseInstr",
                    ],
                ],
            };

            const response = await instance.post(
                pathname + "/api/ticker",
                body,
            );
            notifySuccess("Тикер успешно создан");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    },
);

export const deleteTickerFromGroup = createAsyncThunk(
    "delete/tickerFromGroup",
    async (
        {
            tickerName,
            scheduleId,
            index,
        }: { tickerName: string; scheduleId: string; index: number },
        { rejectWithValue, getState, dispatch },
    ) => {
        const { settings } = getState() as RootState;
        const { name } = settings.activeGroup;

        try {
            const body = {
                group_name: name,
                schedule_id: scheduleId,
                index: index,
            };
            const response = await instance.delete(
                pathname + `/api/ticker/${tickerName}`,
                {
                    data: body,
                },
            );
            notifySuccess(`Тикер ${tickerName} удалён из группы ${name}`);
            dispatch(resetTickerSettings());
            dispatch(resetTicker({ ticker_name: tickerName }));

            dispatch(removeTickerFromGroup(tickerName));
            dispatch(getTickersByGroup(name));
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                if (e.response.data.details) {
                    Error(e.response.data.details);
                }
                if (e.response.data.block) {
                    const splitted = e.response.data.block[0].split(" ");
                    const user_name = splitted[splitted.length - 1];
                    Error(`Тикер заблокирован пользователем ${user_name}`);
                }
                return rejectWithValue(e.message);
            }
        }
    },
);

const groupsSlice = createSlice({
    name: "GroupsSlice",
    initialState,
    reducers: {
        resetState: () => initialState,
        resetGroupTickers(state) {
            state.activeGroupTickers = initialState.activeGroupTickers;
        },
        addTickerToGroup(state, action) {
            state.activeGroupTickers.push(action.payload);
        },
        removeTickerFromGroup(state, action) {
            const tickerName = action.payload;
            state.activeGroupTickers = state.activeGroupTickers.filter(
                (ticker) => ticker.name.ticker_name !== tickerName,
            );
        },
        clearActiveGroupTickers(state) {
            state.activeGroupTickers = [];
        },
    },
    extraReducers: {
        [getGroups.pending.type]: (state) => {
            state.groupsLoading = true;
        },
        [getGroups.rejected.type]: (state, action) => {
            state.groupsLoading = false;
            state.error = action.paylaod;
        },
        [getGroups.fulfilled.type]: (state, action) => {
            state.groupsLoading = false;
            state.error = null;

            action.payload.map((group) => {
                if (Array.isArray(group.name)) {
                    state.currentGroupName = group.name[0];
                }
            });
            state.groups = action.payload;
        },
        [getTickersByGroup.pending.type]: (state) => {
            state.tickersLoading = true;
        },
        [getTickersByGroup.rejected.type]: (state, action) => {
            state.tickersLoading = false;
            state.error = action.paylaod;
        },
        [getTickersByGroup.fulfilled.type]: (state, action) => {
            state.tickersLoading = false;
            state.error = null;
            state.activeGroupTickers = action.payload;
        },
        [getDefaultTickers.fulfilled.type]: (state, action) => {
            state.defaultTickers = action.payload;
        },
        [createGroup.pending.type]: (state) => {
            state.groupsLoading = true;
        },
        [createGroup.rejected.type]: (state, action) => {
            state.groupsLoading = false;
            state.error = action.paylaod;
        },
        [createGroup.fulfilled.type]: (state) => {
            state.groupsLoading = false;
            state.error = null;
        },
        [applyGroup.pending.type]: (state) => {
            state.groupsLoading = true;
        },
        [applyGroup.rejected.type]: (state, action) => {
            state.groupsLoading = false;
            state.error = action.paylaod;
        },
        [applyGroup.fulfilled.type]: (state) => {
            state.groupsLoading = false;
            state.error = null;
        },
    },
});

export const {
    addTickerToGroup,
    removeTickerFromGroup,
    resetGroupTickers,
    clearActiveGroupTickers,
    resetState,
} = groupsSlice.actions;
export default groupsSlice.reducer;
